
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Save, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';

const AdminSiteConfigPricingSettings = () => {
  const { toast } = useToast();
  const { updateConfiguration } = useSiteConfig();
  
  const { value: pricingEnabled, loading: pricingEnabledLoading, refetch: refetchPricingEnabled } = useSiteConfigValue('pricing_section_enabled');
  const { value: pricingCTA, loading: pricingCTALoading, refetch: refetchPricingCTA } = useSiteConfigValue('pricing_cta_text');
  const { value: pricingOffer, loading: pricingOfferLoading, refetch: refetchPricingOffer } = useSiteConfigValue('pricing_special_offer');
  const { value: pricingCurrency, loading: pricingCurrencyLoading, refetch: refetchPricingCurrency } = useSiteConfigValue('pricing_currency');
  
  const [saving, setSaving] = useState(false);
  const [tempValues, setTempValues] = useState({
    enabled: '',
    cta: '',
    offer: '',
    currency: ''
  });

  React.useEffect(() => {
    if (pricingEnabled && !tempValues.enabled) setTempValues(prev => ({ ...prev, enabled: pricingEnabled }));
    if (pricingCTA && !tempValues.cta) setTempValues(prev => ({ ...prev, cta: pricingCTA }));
    if (pricingOffer && !tempValues.offer) setTempValues(prev => ({ ...prev, offer: pricingOffer }));
    if (pricingCurrency && !tempValues.currency) setTempValues(prev => ({ ...prev, currency: pricingCurrency }));
  }, [pricingEnabled, pricingCTA, pricingOffer, pricingCurrency, tempValues]);

  const handleUpdate = async (key: string, value: string, refetch: () => void) => {
    setSaving(true);
    try {
      await updateConfiguration(key, value);
      setTimeout(() => refetch(), 1000);
      toast({
        title: "Pricing Setting Updated",
        description: `${key} has been updated successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update pricing setting.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const loading = pricingEnabledLoading || pricingCTALoading || pricingOfferLoading || pricingCurrencyLoading;

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <DollarSign className="w-5 h-5" />
            <span>Pricing Section Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-white/10 rounded w-1/3"></div>
            <div className="h-10 bg-white/10 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <DollarSign className="w-5 h-5" />
          <span>Pricing Section Settings</span>
          <Badge className="bg-green-600/20 text-green-300 border-green-500/30">
            Website Display
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pricing Section Enabled */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-white">Enable Pricing Section</Label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={tempValues.enabled === 'true'}
                onCheckedChange={(checked) => {
                  const newValue = checked.toString();
                  setTempValues(prev => ({ ...prev, enabled: newValue }));
                  handleUpdate('pricing_section_enabled', newValue, refetchPricingEnabled);
                }}
                disabled={saving}
              />
              {tempValues.enabled === 'true' ? <Eye className="w-4 h-4 text-green-400" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
            </div>
          </div>
          <p className="text-sm text-gray-400">Show or hide the pricing section on the website</p>
        </div>

        {/* CTA Text */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Pricing CTA Button Text</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.cta}
              onChange={(e) => setTempValues(prev => ({ ...prev, cta: e.target.value }))}
              disabled={saving}
              placeholder="Get Started Today"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('pricing_cta_text', tempValues.cta, refetchPricingCTA)}
              disabled={saving || !tempValues.cta.trim() || tempValues.cta === pricingCTA}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Call to action button text in pricing section</p>
        </div>

        {/* Special Offer */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Special Offer Banner</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.offer}
              onChange={(e) => setTempValues(prev => ({ ...prev, offer: e.target.value }))}
              disabled={saving}
              placeholder="Limited time offer..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('pricing_special_offer', tempValues.offer, refetchPricingOffer)}
              disabled={saving || tempValues.offer === pricingOffer}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Special offer text displayed above pricing</p>
        </div>

        {/* Currency Symbol */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Currency Symbol</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.currency}
              onChange={(e) => setTempValues(prev => ({ ...prev, currency: e.target.value }))}
              disabled={saving}
              placeholder="$"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('pricing_currency', tempValues.currency, refetchPricingCurrency)}
              disabled={saving || !tempValues.currency.trim() || tempValues.currency === pricingCurrency}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Currency symbol displayed in pricing</p>
        </div>

        {/* Debug Info */}
        <div className="text-xs text-gray-500 p-3 bg-black/20 rounded">
          <div>Enabled: {pricingEnabled}</div>
          <div>CTA: {pricingCTA}</div>
          <div>Offer: {pricingOffer}</div>
          <div>Currency: {pricingCurrency}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigPricingSettings;
